import * as selectors from '../selectors';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs';
import { EnergyAnalyticsService } from '@twaice-fe/frontend/shared/services';
import * as energyAnalyticsActions from '../actions/energy-analytics.actions';
import { Store } from '@ngrx/store';
const { systemSelectors } = selectors;

@Injectable()
export class EnergyAnalyticsEffects {
  // region Safety Recommendation effects
  fetchSafetyRecommendationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchSafetyRecommendationData),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([, system]) =>
        this.energyAnalyticsService
          .fetchSafetyRecommendationData({ customerBk: system?.customerBk, systemBk: system?.systemBk })
          .pipe(
            map((safetyRecommendationData) =>
              energyAnalyticsActions.loadSafetyRecommendationDataSuccess({ safetyRecommendationData })
            ),
            takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelSafetyRecommendationDataRequest))),
            catchError((error) => {
              console.error('[Error/Fetch safety recommendation]', error);
              return of(energyAnalyticsActions.loadSafetyRecommendationDataFailure({ error }));
            })
          )
      )
    )
  );

  fetchSafetyRecommendationComponentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchSafetyRecommendationComponentDetails),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([params, system]) =>
        this.energyAnalyticsService
          .fetchSafetyRecommendationComponentDetails({
            customerBk: system?.customerBk,
            systemBk: system?.systemBk,
            componentBk: params.componentBk,
          })
          .pipe(
            map((safetyRecommendationComponentDetails) =>
              energyAnalyticsActions.loadSafetyRecommendationComponentDetailsSuccess({ safetyRecommendationComponentDetails })
            ),
            takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelSafetyRecommendationComponentDetailsRequest))),
            catchError((error) => {
              console.error('[Error/Fetch safety recommendation component details]', error);
              return of(energyAnalyticsActions.loadSafetyRecommendationComponentDetailsFailure({ error }));
            })
          )
      )
    )
  );

  fetchSafetyMailNotificationConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchSafetyMailNotificationConfig),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => !!system),
      switchMap(([, system]) =>
        this.energyAnalyticsService
          .fetchMailNotificationConfig({ customerBk: system?.customerBk, systemBk: system?.systemBk })
          .pipe(
            map((notificationSettings) =>
              energyAnalyticsActions.fetchSafetyMailNotificationConfigSuccess({
                notificationsEnabled: notificationSettings.safetyNotificationsEnabled,
              })
            ),
            catchError((error) => {
              console.error('[Error/Fetch safety mail notification config]', error);
              return of(energyAnalyticsActions.fetchSafetyMailNotificationConfigFailure({ error }));
            })
          )
      )
    )
  );

  updateSafetyMailNotificationConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.updateSafetyMailNotificationConfig),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => !!system),
      switchMap(([action, system]) =>
        this.energyAnalyticsService
          .updateSafetyMailNotificationConfig(
            { customerBk: system?.customerBk, systemBk: system?.systemBk },
            action.notificationsEnabled
          )
          .pipe(
            map(() =>
              energyAnalyticsActions.updateSafetyMailNotificationConfigSuccess({
                notificationsEnabled: action.notificationsEnabled,
              })
            ),
            catchError((error) => {
              console.error('[Error/Update safety mail notification config]', error);
              return of(energyAnalyticsActions.updateSafetyMailNotificationConfigFailure({ error }));
            })
          )
      )
    )
  );
  // endregion

  // region  System Ingestion Statistics effects
  fetchSystemIngestionStatisticsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchSystemIngestionStatisticsData),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([, system]) =>
        this.energyAnalyticsService
          .fetchSystemIngestionStatistics({ customerBk: system?.customerBk, systemBk: system?.systemBk })
          .pipe(
            map((statistics) =>
              energyAnalyticsActions.loadSystemIngestionStatisticsSuccess({ systemIngestionStatistics: statistics })
            ),
            takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelSystemIngestionStatisticsRequest))),
            catchError((error) => {
              console.error('[Error/Fetch system ingestion statistics]', error);
              return of(energyAnalyticsActions.loadSystemIngestionStatisticsFailure({ error }));
            })
          )
      )
    )
  );
  // endregion

  // region Health KPIs
  fetchLatestHealthKPIData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchLatestHealthKPIData),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([props, system]) =>
        this.energyAnalyticsService
          .fetchLatestHealthKPIData(props.kpi, { customerBk: system?.customerBk, systemBk: system?.systemBk, ...props.params })
          .pipe(
            map((healthKPIData) => energyAnalyticsActions.loadLatestHealthKPIDataSuccess(healthKPIData)),
            catchError((error) => {
              console.error('[Error/Fetch Health KPIs]', error);
              return of(energyAnalyticsActions.loadLatestHealthKPIDataFailure({ kpi: props.kpi, error }));
            }),
            takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelHealthKPIDataRequest)))
          )
      )
    )
  );

  fetchAggregatedHealthKPIData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchAggregatedHealthKPIData),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([props, system]) =>
        this.energyAnalyticsService
          .fetchAggregatedHealthKPIData(props.kpi, {
            customerBk: system?.customerBk,
            systemBk: system?.systemBk,
            ...props.params,
          })
          .pipe(
            map((healthKPIData) => energyAnalyticsActions.loadAggregatedHealthKPIDataSuccess(healthKPIData)),
            catchError((error) => {
              console.error('[Error/Fetch Health KPIs]', error);
              return of(energyAnalyticsActions.loadAggregatedHealthKPIDataFailure({ kpi: props.kpi, error }));
            }),
            takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelHealthKPIDataRequest)))
          )
      )
    )
  );

  // endregion

  // region  has siblings effect effects
  fetchHasSiblingEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(energyAnalyticsActions.fetchHasSiblingEffect),
      withLatestFrom(this.store.select(systemSelectors.getSelected)),
      filter(([, system]) => system != null),
      switchMap(([, system]) =>
        this.energyAnalyticsService.fetchHasSiblingEffect({ customerBk: system?.customerBk, systemBk: system?.systemBk }).pipe(
          map((hasSiblingEffect) => energyAnalyticsActions.loadHasSiblingEffectSuccess({ hasSiblingEffect })),
          takeUntil(this.actions$.pipe(ofType(energyAnalyticsActions.cancelHasSiblingEffectRequest))),
          catchError((error) => {
            console.error('[Error/Fetch has siblings effect]', error);
            return of(energyAnalyticsActions.loadHasSiblingEffectFailure({ error }));
          })
        )
      )
    )
  );
  // endregion

  constructor(
    private readonly actions$: Actions,
    private energyAnalyticsService: EnergyAnalyticsService,
    protected store: Store
  ) {}
}
